import { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Calendar,
  Clock,
  Users,
  Loader2,
  CheckCircle,
  AlertCircle,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  X,
  MapPin,
  Megaphone,
  Info,
  UtensilsCrossed,
  Minus,
  Plus,
} from "lucide-react";
import {
  format,
  addDays,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isBefore,
  isAfter,
  startOfDay,
  startOfWeek,
  addMonths,
  subMonths,
  getDay,
} from "date-fns";
import {
  getOrderedCountries,
  defaultCountryCodeForTimezone,
} from "@reservamanager/shared-types";
import AppDownloadCta from "./components/AppDownloadCta";
import PoweredBy from "./components/PoweredBy";
import LanguageSwitcher from "./components/LanguageSwitcher";
import { useT } from "./i18n";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:4000/api";

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

// Sede pública dentro de `organization.locations` (recorte del backend, ver
// `GET /public/organizations/:slug`). Solo se tipan los campos que usa el
// widget (timezone, para el prefijo telefónico por defecto).
interface OrganizationLocation {
  id: string;
  timezone: string;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  phone: string | null;
  address: string | null;
  logo: string | null;
  // Aviso público del negocio ("Eventos y novedades" de la centralita).
  // null = sin aviso. Puede ser multilínea.
  announcement: string | null;
  // Sede activa para este slug + listado de sedes de la organización (con su
  // timezone). Se usan para derivar el prefijo telefónico por defecto.
  locationId?: string;
  locations?: OrganizationLocation[];
}

interface TimeSlot {
  time: string;
  available: boolean;
}

interface DayAvailability {
  date: string;
  isOpen: boolean;
  reason?: string;
  availability?: {
    comida: {
      available: boolean;
      blockedTables: number;
      occupiedTables: number;
    };
    cena: { available: boolean; blockedTables: number; occupiedTables: number };
  };
  // Salones (Room) deshabilitados ese día concreto (ej: solo abrir un salón
  // por un evento). Se usa para ocultar esos salones del selector de sala.
  disabledRoomIds?: string[];
}

interface Room {
  id: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isDefault: boolean;
}

interface ScheduleConfig {
  maxAdvanceDays: number;
  maxPartySize: number;
  largeGroupThreshold: number;
  notesPlaceholder: string | null;
  // El restaurante guarda el importe de la cuenta en la ficha: hay que avisar.
  spendNotice?: boolean;
  shifts: Array<{ name: string; startTime: string; endTime: string }>;
}

// Menú del día ofrecido por el negocio para una fecha concreta (feature
// "menús por fechas"). `description` es contenido libre del negocio, NUNCA
// se traduce. `scope: "all_guests"` fuerza cantidad = comensales de la
// reserva (checkbox único); `"per_guest"` permite un stepper 0..comensales.
interface MenuOption {
  id: string;
  name: string;
  description: string | null;
  price: number;
  scope: "per_guest" | "all_guests";
  // El servidor ya filtra por `partySize` (ver `handleContinueFromSelection`);
  // este campo es solo informativo, no se usa para filtrar en cliente.
  minPartySize?: number;
}

// Paso "menu" es SIEMPRE saltable: solo se entra en él si `/menus?date=` del
// día elegido devuelve al menos un menú (ver `handleContinueFromSelection`).
// Sin menús ese día, el flujo va directo "selection" → "details", igual que
// antes de esta feature.
type Step = "selection" | "menu" | "details" | "success";

export default function App() {
  const { slug } = useParams<{ slug: string }>();
  // Campaña de email de origen (`?c=` del botón "Reservar" de una campaña). Se
  // reenvía al crear la reserva SOLO para atribuir la conversión; sin él todo
  // funciona exactamente igual.
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get("c");

  const { lang, t, tPeople, tPeopleShort, tSplit, formatDate, dateLocale, weekdays } =
    useT();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [step, setStep] = useState<Step>("selection");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [partySize, setPartySize] = useState<number>(2);
  const [isLargeGroup, setIsLargeGroup] = useState(false);
  const [customPartySize, setCustomPartySize] = useState<string>("");
  const [customerName, setCustomerName] = useState("");
  const [customerLastName, setCustomerLastName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [customerPhone, setCustomerPhone] = useState("");
  // Se inicializa en "+34" (fallback de `defaultCountryCodeForTimezone`) y se
  // recalcula en cuanto llega `organization` con la timezone de la sede activa.
  const [customerCountryCode, setCustomerCountryCode] = useState("+34");
  const [notes, setNotes] = useState("");

  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);

  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  // Salones deshabilitados para `selectedDate`, tal como los devuelve la
  // respuesta de `/slots` (redundante con `calendarAvailability[selectedDate]
  // .disabledRoomIds`, que ya trae lo mismo con antelación desde el calendario
  // mensual; se combinan ambas fuentes como red de seguridad).
  const [slotsDisabledRoomIds, setSlotsDisabledRoomIds] = useState<string[]>(
    [],
  );
  // Motivo de cierre del día elegido, tal como lo devuelve `/slots` cuando
  // `isClosed: true` (ya viene resuelto por el backend: cae a "Día de cierre"
  // si el override no tiene motivo propio). Sirve de red de seguridad para el
  // caso en que el calendario mensual aún no haya cargado ese día y el
  // cliente haya podido pulsarlo antes de que `isDateSelectable` lo bloquee.
  const [dayClosedReason, setDayClosedReason] = useState<string | null>(null);

  const [calendarAvailability, setCalendarAvailability] = useState<
    Record<string, DayAvailability>
  >({});
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isLargeGroupResponse, setIsLargeGroupResponse] = useState(false);
  const [reservationCode, setReservationCode] = useState<string | null>(null);
  const [reservationStatus, setReservationStatus] = useState<string | null>(
    null,
  );
  // Token opaco de acceso a las notas (alergias) de la reserva recién creada.
  // Solo llega en la respuesta 201 de esta pantalla; hay que colarlo en el
  // enlace "Gestionar mi reserva" o el cliente pierde el acceso a sus propias
  // notas al navegar a `/r/:code`.
  const [reservationNotesToken, setReservationNotesToken] = useState<
    string | null
  >(null);

  const [showFullCalendar, setShowFullCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Menús por fechas (paso opcional entre "selection" y "details"). Se
  // rellena SOLO al pulsar "Continuar" con fecha+hora ya elegidas — ver
  // `handleContinueFromSelection`. `menuQuantities` mapea `menuId` →
  // cantidad elegida (0 = no incluido en el envío). `wantsMenu` distingue
  // la pregunta "¿Queréis menú?" (null = sin responder) de la lista.
  const [availableMenus, setAvailableMenus] = useState<MenuOption[]>([]);
  const [loadingMenus, setLoadingMenus] = useState(false);
  const [wantsMenu, setWantsMenu] = useState<boolean | null>(null);
  const [menuQuantities, setMenuQuantities] = useState<Record<string, number>>(
    {},
  );
  const [expandedMenuId, setExpandedMenuId] = useState<string | null>(null);

  const today = new Date();
  const maxAdvanceDays = scheduleConfig?.maxAdvanceDays || 30;
  const maxPartySize = scheduleConfig?.maxPartySize || 15;
  const largeGroupThreshold = scheduleConfig?.largeGroupThreshold ?? 15;
  const maxDate = addDays(today, maxAdvanceDays);

  const upcomingDays = useMemo(() => {
    // Start from Monday of the current week so the grid is always Mon→Sun aligned
    const mondayOfThisWeek = startOfWeek(today, { weekStartsOn: 1 });
    const endDate = addDays(today, Math.min(maxAdvanceDays, 14));
    const days: Date[] = [];
    let current = mondayOfThisWeek;
    while (current <= endDate) {
      days.push(current);
      current = addDays(current, 1);
    }
    return days;
  }, [maxAdvanceDays]);

  const effectivePartySize = isLargeGroup
    ? parseInt(customPartySize) || 0
    : partySize;

  useEffect(() => {
    if (!slug) return;
    const loadOrganization = async () => {
      try {
        const res = await fetch(`${API_URL}/public/organizations/${slug}`);
        const data = await res.json();
        if (!res.ok) {
          setError(data.message || t("appOrgNotFoundFallback"));
          return;
        }
        setOrganization(data.organization);
        if (data.config) setScheduleConfig(data.config);
        // Load rooms for room selection
        if (data.rooms && data.rooms.length > 0) {
          setRooms(data.rooms);
          // Select default room first, or first room if none is default
          const defaultRoom = data.rooms.find((r: Room) => r.isDefault);
          setSelectedRoomId(defaultRoom ? defaultRoom.id : data.rooms[0].id);
        }
      } catch {
        setError(t("appOrgLoadNetworkError"));
      } finally {
        setLoading(false);
      }
    };
    loadOrganization();
  }, [slug]);

  // Prefijo telefónico por defecto según la timezone de la sede activa (ej:
  // un restaurante en México ve "+52" en vez de "+34" al abrir el widget).
  const defaultCountryCode = useMemo(() => {
    const activeLocation = organization?.locations?.find(
      (loc) => loc.id === organization.locationId,
    );
    return defaultCountryCodeForTimezone(activeLocation?.timezone);
  }, [organization]);

  // Catálogo completo del selector de prefijo, ordenado con el de la sede
  // primero. Depende del default de la sede (fijo tras cargar), NO de lo que
  // el cliente vaya eligiendo — si dependiera de `customerCountryCode` la
  // lista se reordenaría bajo el cursor cada vez que el cliente cambiara de
  // prefijo.
  const orderedCountries = useMemo(
    () => getOrderedCountries(defaultCountryCode),
    [defaultCountryCode],
  );

  // Se aplica el prefijo por defecto en cuanto se conoce (una vez, al cargar
  // la organización), sin pisar una elección manual posterior del cliente.
  useEffect(() => {
    if (!organization) return;
    setCustomerCountryCode(defaultCountryCode);
  }, [organization, defaultCountryCode]);

  useEffect(() => {
    if (!slug) return;
    const loadAvailability = async () => {
      setLoadingAvailability(true);
      try {
        // Start from today (not tomorrow) to show today's availability
        const startDate = format(today, "yyyy-MM-dd");
        const endDate = format(addDays(today, maxAdvanceDays), "yyyy-MM-dd");
        // Include roomId if a specific room is selected
        const roomParam = selectedRoomId ? `&roomId=${selectedRoomId}` : "";
        // Include partySize to filter by table capacity
        const partySizeParam =
          effectivePartySize > 0 ? `&partySize=${effectivePartySize}` : "";
        const res = await fetch(
          `${API_URL}/public/organizations/${slug}/calendar?startDate=${startDate}&endDate=${endDate}${roomParam}${partySizeParam}`,
        );
        const data = await res.json();
        if (res.ok && data.days) {
          const newMap: Record<string, DayAvailability> = {};
          data.days.forEach((day: DayAvailability) => {
            newMap[day.date] = day;
          });
          setCalendarAvailability(newMap);
        }
      } catch (err) {
        console.error("Error loading calendar availability:", err);
      } finally {
        setLoadingAvailability(false);
      }
    };
    loadAvailability();
  }, [slug, maxAdvanceDays, selectedRoomId, effectivePartySize]);

  useEffect(() => {
    if (!selectedDate || !slug) return;
    // Guarda contra respuestas obsoletas: el auto-cambio de salón (más abajo)
    // puede disparar una 2ª petición justo después de esta (mismo día, salón
    // distinto) — si la respuesta VIEJA llega después que la nueva, no debe
    // pisar `timeSlots`/`slotsDisabledRoomIds` con datos de un salón/fecha que
    // ya no es el vigente. React aborta automáticamente el `controller` de la
    // petición anterior al re-ejecutar este efecto (cleanup), así que solo la
    // última petición lanzada puede llegar a escribir estado.
    const controller = new AbortController();
    const loadSlots = async () => {
      setLoadingSlots(true);
      setSelectedTime("");
      setSlotsDisabledRoomIds([]);
      setDayClosedReason(null);
      try {
        // Include roomId if a specific room is selected
        const roomParam = selectedRoomId ? `&roomId=${selectedRoomId}` : "";
        // Include partySize to filter by table capacity
        const partySizeParam =
          effectivePartySize > 0 ? `&partySize=${effectivePartySize}` : "";
        const res = await fetch(
          `${API_URL}/public/organizations/${slug}/slots?date=${selectedDate}${roomParam}${partySizeParam}`,
          { signal: controller.signal },
        );
        const data = await res.json();
        if (res.ok) {
          setTimeSlots(data.slots || []);
          setSlotsDisabledRoomIds(data.disabledRoomIds || []);
          setDayClosedReason(
            data.isClosed ? data.closedReason || t("appDefaultClosedReason") : null,
          );
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("Error loading slots:", err);
      } finally {
        if (!controller.signal.aborted) setLoadingSlots(false);
      }
    };
    loadSlots();
    return () => controller.abort();
  }, [selectedDate, slug, selectedRoomId, effectivePartySize]);

  // Salones deshabilitados para la fecha elegida (feature Room por fecha, ej:
  // una feria en la que solo abre un salón). Se combina lo que ya trae el
  // calendario mensual (cargado de antemano, sin depender de que se elija una
  // fecha) con lo que trae la respuesta de `/slots` del día concreto una vez
  // llega — ambas fuentes deberían coincidir, se unen como red de seguridad
  // por si el calendario aún no cargó ese día. Antes de elegir una fecha, el
  // resultado es siempre vacío y el selector de salón muestra todos los
  // salones, igual que antes de este cambio.
  const disabledRoomIdsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    const fromCalendar = calendarAvailability[selectedDate]?.disabledRoomIds ?? [];
    return Array.from(new Set([...fromCalendar, ...slotsDisabledRoomIds]));
  }, [selectedDate, calendarAvailability, slotsDisabledRoomIds]);

  const availableRoomsForDate = useMemo(
    () => rooms.filter((r) => !disabledRoomIdsForSelectedDate.includes(r.id)),
    [rooms, disabledRoomIdsForSelectedDate],
  );

  // Salón preferido de una lista de disponibles: el marcado como `isDefault`
  // si sigue disponible, si no el primero de la lista.
  const pickFallbackRoom = (list: Room[]): Room | undefined =>
    list.find((r) => r.isDefault) ?? list[0];

  // Dos casos a cubrir al cambiar de fecha (o de disponibilidad de salones):
  // 1) el salón seleccionado queda deshabilitado ese día → se cambia
  //    automáticamente al salón preferido disponible (o a ninguno si no
  //    queda ninguno habilitado).
  // 2) no había ningún salón seleccionado (por haber pasado antes por una
  //    fecha sin salones disponibles) y la fecha actual SÍ tiene salones
  //    disponibles → se selecciona uno automáticamente, en vez de dejar
  //    `selectedRoomId` en `null` para siempre.
  useEffect(() => {
    if (!selectedDate) return;
    if (
      selectedRoomId &&
      disabledRoomIdsForSelectedDate.includes(selectedRoomId)
    ) {
      setSelectedRoomId(pickFallbackRoom(availableRoomsForDate)?.id ?? null);
    } else if (!selectedRoomId && availableRoomsForDate.length > 0) {
      setSelectedRoomId(pickFallbackRoom(availableRoomsForDate)?.id ?? null);
    }
  }, [selectedDate, disabledRoomIdsForSelectedDate, availableRoomsForDate, selectedRoomId]);

  // NOTA (23-jul-2026, RGPD): aquí había un autocompletado de nombre/apellidos/email
  // al teclear el teléfono, contra un endpoint público SIN autenticación
  // (`/public/organizations/:slug/customer-lookup`) que devolvía los datos de
  // cualquier cliente ya registrado con ese número. Un dictamen de cumplimiento
  // detectó que eso permitía a cualquiera comprobar si un teléfono es cliente de
  // un restaurante y obtener su nombre/email sin ninguna autenticación. El
  // endpoint se retiró del backend y esta llamada con él. NO reintroducir sin
  // rediseñar el lookup para que requiera algo más que el teléfono en claro.

  const isDateSelectable = (date: Date): boolean => {
    if (isBefore(startOfDay(date), startOfDay(today))) return false;
    const dateStr = format(date, "yyyy-MM-dd");
    const dayAvailability = calendarAvailability[dateStr];
    if (dayAvailability && !dayAvailability.isOpen) return false;
    return true;
  };

  const groupedSlots = useMemo(() => {
    // Get the availability for the selected date
    const dayAvailability = selectedDate
      ? calendarAvailability[selectedDate]
      : null;

    if (!scheduleConfig?.shifts || scheduleConfig.shifts.length === 0) {
      const lunch = timeSlots.filter(
        (s) => parseInt(s.time.split(":")[0]) < 17,
      );
      const dinner = timeSlots.filter(
        (s) => parseInt(s.time.split(":")[0]) >= 17,
      );

      // Check if shifts are fully booked using calendar availability data
      const lunchFull =
        dayAvailability?.availability?.comida?.available === false;
      const dinnerFull =
        dayAvailability?.availability?.cena?.available === false;

      // Also check if all slots in the shift are unavailable
      const lunchAllUnavailable =
        lunch.length > 0 && lunch.every((s) => !s.available);
      const dinnerAllUnavailable =
        dinner.length > 0 && dinner.every((s) => !s.available);

      return [
        {
          name: t("appDefaultShiftLunch"),
          slots: lunch,
          isFull: lunchFull || lunchAllUnavailable,
        },
        {
          name: t("appDefaultShiftDinner"),
          slots: dinner,
          isFull: dinnerFull || dinnerAllUnavailable,
        },
      ].filter((g) => g.slots.length > 0 || g.isFull);
    }
    return scheduleConfig.shifts
      .map((shift) => {
        const startHour = parseInt(shift.startTime.split(":")[0]);
        const endHour = parseInt(shift.endTime.split(":")[0]);
        const slots = timeSlots.filter((s) => {
          const hour = parseInt(s.time.split(":")[0]);
          return hour >= startHour && hour < endHour + 1;
        });

        // Determine shift type for availability check
        const shiftType = startHour < 17 ? "comida" : "cena";
        const shiftFull =
          dayAvailability?.availability?.[shiftType]?.available === false;
        const allUnavailable =
          slots.length > 0 && slots.every((s) => !s.available);

        return {
          name: shift.name.toUpperCase(),
          slots,
          isFull: shiftFull || allUnavailable,
        };
      })
      .filter((g) => g.slots.length > 0 || g.isFull);
  }, [timeSlots, scheduleConfig, selectedDate, calendarAvailability, lang]);

  // Menús por fechas: se pide SOLO tras fecha+hora definitivas, al pulsar
  // "Continuar" en el paso "selection". Si el día no tiene menús (o falla la
  // petición), se salta directo a "details" — el flujo queda EXACTAMENTE
  // como antes de esta feature en ese caso.
  const handleContinueFromSelection = async () => {
    if (!selectedDate || !selectedTime || !slug) return;
    setLoadingMenus(true);
    try {
      // El backend filtra en servidor los menús con `minPartySize` mayor que
      // los comensales de la reserva (no duplicar esa lógica aquí).
      const partySizeParam =
        effectivePartySize > 0 ? `&partySize=${effectivePartySize}` : "";
      const res = await fetch(
        `${API_URL}/public/organizations/${slug}/menus?date=${selectedDate}${partySizeParam}`,
      );
      const data = await res.json();
      if (
        res.ok &&
        data.success &&
        Array.isArray(data.menus) &&
        data.menus.length > 0
      ) {
        setAvailableMenus(data.menus);
        setMenuQuantities({});
        setWantsMenu(null);
        setExpandedMenuId(null);
        setStep("menu");
      } else {
        setAvailableMenus([]);
        setMenuQuantities({});
        setStep("details");
      }
    } catch {
      setAvailableMenus([]);
      setMenuQuantities({});
      setStep("details");
    } finally {
      setLoadingMenus(false);
    }
  };

  const totalMenuQuantity = useMemo(
    () => Object.values(menuQuantities).reduce((sum, q) => sum + q, 0),
    [menuQuantities],
  );
  const menuQuantityExceedsPartySize = totalMenuQuantity > effectivePartySize;

  // El stepper de "per_guest" nunca deja que la SUMA total supere los
  // comensales (clamp al incrementar). El checkbox de "all_guests" fija la
  // cantidad directamente a `effectivePartySize` sin pasar por este clamp —
  // por eso el total puede superarlo si ya había otras cantidades elegidas,
  // de ahí el aviso/validación `menuQuantityExceedsPartySize` más abajo.
  const incrementMenuQuantity = (menuId: string) => {
    setMenuQuantities((prev) => {
      const total = Object.values(prev).reduce((sum, q) => sum + q, 0);
      if (total >= effectivePartySize) return prev;
      return { ...prev, [menuId]: (prev[menuId] || 0) + 1 };
    });
  };

  const decrementMenuQuantity = (menuId: string) => {
    setMenuQuantities((prev) => {
      const current = prev[menuId] || 0;
      if (current <= 0) return prev;
      return { ...prev, [menuId]: current - 1 };
    });
  };

  const setAllGuestsMenuQuantity = (menuId: string, checked: boolean) => {
    setMenuQuantities((prev) => ({
      ...prev,
      [menuId]: checked ? effectivePartySize : 0,
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    const fullName = `${customerName} ${customerLastName}`.trim();
    const menuSelectionsPayload = Object.entries(menuQuantities)
      .filter(([, quantity]) => quantity > 0)
      .map(([menuId, quantity]) => ({ menuId, quantity }));
    try {
      const res = await fetch(
        `${API_URL}/public/organizations/${slug}/reservations`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customerName: fullName,
            customerEmail,
            customerPhone,
            customerCountryCode,
            date: selectedDate,
            time: selectedTime,
            partySize: effectivePartySize,
            notes,
            acceptsMarketing: acceptMarketing,
            notifyByWhatsApp: true,
            // Idioma elegido por el cliente en el widget. El backend lo
            // valida con zod (default "es") — si aún no está desplegado el
            // campo se ignora sin romper nada.
            language: lang,
            ...(campaignId ? { campaignId } : {}),
            ...(menuSelectionsPayload.length > 0
              ? { menuSelections: menuSelectionsPayload }
              : {}),
          }),
        },
      );
      const data = await res.json();
      if (!res.ok) {
        setSubmitError(data.message || t("appSubmitErrorFallback"));
        return;
      }
      setIsLargeGroupResponse(data.isLargeGroup || false);
      setReservationCode(data.reservation?.confirmationCode || null);
      setReservationStatus(data.reservation?.status || null);
      setReservationNotesToken(data.reservation?.notesToken || null);
      setStep("success");
    } catch {
      setSubmitError(t("commonConnectionErrorRetry"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {t("appOrgNotFoundHeading")}
          </h1>
          <p className="text-gray-600">
            {error || t("appOrgLoadGenericError")}
          </p>
        </div>
      </div>
    );
  }

  if (step === "success") {
    const isPending = reservationStatus === "pending" && !isLargeGroupResponse;
    const handleNewReservation = () => {
      // Reset to start of flow
      setStep("selection");
      setSelectedDate("");
      setSelectedTime("");
      setReservationCode(null);
      setReservationStatus(null);
      setReservationNotesToken(null);
      setIsLargeGroupResponse(false);
      setSubmitError(null);
      setAvailableMenus([]);
      setMenuQuantities({});
      setWantsMenu(null);
      setExpandedMenuId(null);
    };
    const menuSummaryItems = availableMenus
      .filter((menu) => (menuQuantities[menu.id] || 0) > 0)
      .map((menu) => ({
        id: menu.id,
        name: menu.name,
        quantity: menuQuantities[menu.id],
      }));
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle
            className={`w-16 h-16 mx-auto mb-4 ${
              isLargeGroupResponse || isPending
                ? "text-amber-500"
                : "text-green-500"
            }`}
          />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {isLargeGroupResponse
              ? t("appSuccessHeadingLargeGroup")
              : isPending
                ? t("appSuccessHeadingPending")
                : t("appSuccessHeadingConfirmed")}
          </h1>
          <p className="text-gray-600 mb-6">
            {(() => {
              const key = isLargeGroupResponse
                ? "appSuccessBodyLargeGroup"
                : isPending
                  ? "appSuccessBodyPending"
                  : "appSuccessBodyConfirmed";
              const [before, after] = tSplit(key, "name");
              return (
                <>
                  {before}
                  <strong>{organization.name}</strong>
                  {after}
                </>
              );
            })()}
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span>
                {formatDate(new Date(selectedDate + "T12:00:00"), "long")}
              </span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <span>{selectedTime}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-gray-500" />
              <span>{tPeople(effectivePartySize)}</span>
            </div>
          </div>
          {menuSummaryItems.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
              <p className="text-sm font-semibold text-gray-700 mb-1">
                {t("appMenuSummaryHeading")}
              </p>
              <ul className="text-sm text-gray-600 space-y-0.5">
                {menuSummaryItems.map((item) => (
                  <li key={item.id}>
                    {t("commonMenuSummaryItem", {
                      qty: item.quantity,
                      name: item.name,
                    })}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-sm text-gray-500 mb-6">
            {(() => {
              const [before, after] = tSplit(
                isPending ? "appSuccessEmailNotePending" : "appSuccessEmailNoteConfirmed",
                "email",
              );
              return (
                <>
                  {before}
                  <strong>{customerEmail}</strong>
                  {after}
                </>
              );
            })()}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleNewReservation}
              className="flex-1 bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-4 rounded-xl shadow"
            >
              {t("appNewReservationButton")}
            </button>
            {reservationCode && (
              <a
                href={`/r/${reservationCode}${
                  reservationNotesToken
                    ? `?t=${encodeURIComponent(reservationNotesToken)}`
                    : ""
                }`}
                className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-3 px-4 rounded-xl text-center"
              >
                Gestionar mi reserva
              </a>
            )}
          </div>
          <AppDownloadCta />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-3">
      <div className="max-w-lg mx-auto">
        <div className="flex justify-end mb-2">
          <LanguageSwitcher />
        </div>

        <div className="text-center mb-6">
          {organization.logo ? (
            <img
              src={organization.logo}
              alt={organization.name}
              className="h-32 max-w-[280px] object-contain mx-auto mb-3"
            />
          ) : (
            <div className="w-20 h-20 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-white text-3xl font-bold">
                {organization.name.charAt(0)}
              </span>
            </div>
          )}
          <h1 className="text-xl font-bold text-gray-800">
            {organization.name}
          </h1>
        </div>

        {/* Aviso del negocio ("Eventos y novedades" de la centralita).
            Vive en la cabecera (no dentro de un paso del wizard), así que se
            ve tanto en "selección" como en "detalles". */}
        {organization.announcement && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 shadow-sm">
            <Megaphone className="w-5 h-5 flex-shrink-0 text-amber-500" />
            <p className="whitespace-pre-line leading-snug">
              {organization.announcement}
            </p>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {step === "selection" && (
            <div className="p-4 space-y-5">
              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                  <Users className="w-4 h-4" />
                  {t("appPartySizeLabel")}
                </label>

                {/* Number buttons grid */}
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {Array.from(
                    { length: Math.min(maxPartySize, largeGroupThreshold) },
                    (_, i) => i + 1,
                  ).map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => {
                        setPartySize(n);
                        setIsLargeGroup(false);
                        setCustomPartySize("");
                      }}
                      className={`h-11 rounded-lg font-medium text-sm transition-all ${
                        partySize === n && !isLargeGroup
                          ? "bg-primary-600 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>

                {/* Large group option — only if maxPartySize > largeGroupThreshold */}
                {maxPartySize > largeGroupThreshold && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsLargeGroup(true);
                      setPartySize(0);
                    }}
                    className={`w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all ${
                      isLargeGroup
                        ? "bg-secondary-500 text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    <UserPlus className="w-4 h-4" />
                    {t("appLargeGroupButton", { threshold: largeGroupThreshold })}
                  </button>
                )}

                {maxPartySize > largeGroupThreshold && isLargeGroup && (
                  <div className="mt-3 p-3 bg-secondary-50 rounded-lg border border-secondary-200">
                    <label className="block text-sm font-medium text-secondary-800 mb-2">
                      {t("appLargeGroupQuestion")}
                    </label>
                    <input
                      type="number"
                      min={largeGroupThreshold + 1}
                      max={maxPartySize}
                      value={customPartySize}
                      onChange={(e) => setCustomPartySize(e.target.value)}
                      placeholder={t("appLargeGroupPlaceholder")}
                      className="w-full px-4 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-secondary-500 text-center font-medium"
                    />
                    <p className="text-xs text-secondary-700 mt-2">
                      {t("commonRestaurantConfirmAvailability")}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                  <Calendar className="w-4 h-4" />
                  {t("appSelectDayLabel")}
                </label>
                {loadingAvailability ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-7 gap-2">
                      {upcomingDays.map((day) => {
                        const dateStr = format(day, "yyyy-MM-dd");
                        const isSelected = selectedDate === dateStr;
                        const isSelectable = isDateSelectable(day);
                        const dayAvailability = calendarAvailability[dateStr];
                        return (
                          <button
                            key={dateStr}
                            type="button"
                            onClick={() =>
                              isSelectable && setSelectedDate(dateStr)
                            }
                            disabled={!isSelectable}
                            className={`py-2 rounded-xl text-center transition-all ${
                              isSelected
                                ? "bg-primary-500 text-white shadow-md ring-2 ring-primary-600"
                                : isSelectable
                                  ? "bg-primary-100 text-gray-700 hover:bg-primary-200"
                                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
                            }`}
                            title={dayAvailability?.reason || undefined}
                          >
                            <div className="text-lg font-bold">
                              {format(day, "d")}
                            </div>
                            <div className="text-xs font-medium uppercase">
                              {format(day, "EEE", { locale: dateLocale })
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {maxAdvanceDays > 14 && (
                      <button
                        type="button"
                        onClick={() => setShowFullCalendar(true)}
                        className="mt-3 w-full py-2 text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center justify-center gap-1 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        <Calendar className="w-4 h-4" />
                        {t("appShowMoreDates")}
                      </button>
                    )}
                  </>
                )}
                {selectedDate && (
                  <div className="mt-2 text-center">
                    <span className="text-sm text-primary-700 font-medium">
                      {formatDate(new Date(selectedDate + "T12:00:00"), "long")}
                    </span>
                  </div>
                )}
              </div>

              {/* Room selector: SOLO tras elegir día, porque cada salón puede
                  estar deshabilitado según la fecha (disabledRoomIds) y antes
                  de elegirla no se sabe cuáles ofrecer. Con 2+ salones
                  disponibles se muestra para elegir; con 1 solo salón se
                  muestra ÚNICAMENTE si queda deshabilitado ese día (aviso de
                  "no hay salones"), porque si no, no hay elección real. */}
              {selectedDate &&
                (rooms.length > 1 ||
                  (rooms.length === 1 &&
                    availableRoomsForDate.length === 0)) && (
                  <div>
                    {rooms.length > 1 && (
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                        <MapPin className="w-4 h-4" />
                        {t("appSelectRoomLabel")}
                      </label>
                    )}
                    {availableRoomsForDate.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {availableRoomsForDate.map((room) => (
                          <button
                            key={room.id}
                            type="button"
                            onClick={() => setSelectedRoomId(room.id)}
                            className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                              selectedRoomId === room.id
                                ? "bg-primary-600 text-white shadow-md"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {room.name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 flex items-center gap-2">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        {t("appNoRoomsAvailable")}
                      </p>
                    )}
                  </div>
                )}

              {selectedDate && (
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                    <Clock className="w-4 h-4" />
                    {t("appSelectTimeLabel")}
                  </label>
                  {/* Motivo del día: horario especial pero el día sigue
                      abierto (ej. override con "Evento Dibulandia"). Solo
                      informa, no bloquea nada. */}
                  {calendarAvailability[selectedDate]?.isOpen &&
                    calendarAvailability[selectedDate]?.reason && (
                      <div className="mb-3 flex items-start gap-2 rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-700">
                        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>{calendarAvailability[selectedDate]?.reason}</span>
                      </div>
                    )}
                  {loadingSlots ? (
                    <div className="flex justify-center py-6">
                      <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                    </div>
                  ) : timeSlots.length === 0 ? (
                    <div className="text-center py-6 text-gray-500 flex flex-col items-center gap-2 px-2">
                      {dayClosedReason ? (
                        <>
                          <AlertCircle className="w-5 h-5 text-gray-400" />
                          <span>{dayClosedReason}</span>
                        </>
                      ) : (
                        <span>{t("appNoSlotsAvailable")}</span>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {groupedSlots.map((group) => (
                        <div key={group.name}>
                          <div className="text-xs font-semibold text-gray-500 mb-2 text-center underline">
                            {group.name}
                          </div>
                          {group.isFull ? (
                            <div className="flex items-center justify-center py-4 bg-gray-100 rounded-lg">
                              <span className="text-gray-500 font-semibold text-sm">
                                {t("appNoTablesAvailable")}
                              </span>
                            </div>
                          ) : (
                            <div className="grid grid-cols-3 gap-2">
                              {group.slots.map((slot) => (
                                <button
                                  key={slot.time}
                                  type="button"
                                  onClick={() =>
                                    slot.available && setSelectedTime(slot.time)
                                  }
                                  disabled={!slot.available}
                                  className={`py-3 rounded-lg text-sm font-semibold transition-all ${
                                    selectedTime === slot.time
                                      ? "bg-primary-500 text-white shadow-md ring-2 ring-primary-600"
                                      : slot.available
                                        ? "bg-primary-100 text-primary-700 hover:bg-primary-200"
                                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                  }`}
                                >
                                  {slot.time}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={handleContinueFromSelection}
                disabled={
                  !selectedDate ||
                  !selectedTime ||
                  loadingMenus ||
                  (isLargeGroup &&
                    (!customPartySize ||
                      parseInt(customPartySize) < largeGroupThreshold + 1))
                }
                className="w-full py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed uppercase tracking-wide flex items-center justify-center gap-2"
              >
                {loadingMenus && (
                  <Loader2 className="w-4 h-4 animate-spin" />
                )}
                {t("appContinueButton")}
              </button>
            </div>
          )}

          {step === "menu" && (
            <div className="p-4 space-y-4">
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-primary-800">
                    <Calendar className="w-4 h-4" />
                    <span className="font-medium">
                      {formatDate(new Date(selectedDate + "T12:00:00"), "short")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-primary-800">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">{selectedTime}</span>
                  </div>
                  <div className="flex items-center gap-2 text-primary-800">
                    <Users className="w-4 h-4" />
                    <span className="font-medium">
                      {tPeopleShort(effectivePartySize)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setStep("selection")}
                  className="text-xs text-primary-600 hover:underline mt-2"
                >
                  {t("appSummaryModify")}
                </button>
              </div>

              {wantsMenu === null && (
                <div className="text-center space-y-4 py-2">
                  <div className="flex flex-col items-center gap-2">
                    <UtensilsCrossed className="w-8 h-8 text-primary-600" />
                    <h2 className="text-base font-semibold text-gray-800">
                      {t("appMenuQuestionHeading")}
                    </h2>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setMenuQuantities({});
                        setStep("details");
                      }}
                      className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                    >
                      {t("appMenuNoThanksButton")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setWantsMenu(true)}
                      className="flex-1 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700"
                    >
                      {t("appMenuYesButton")}
                    </button>
                  </div>
                </div>
              )}

              {wantsMenu && (
                <div className="space-y-4">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <UtensilsCrossed className="w-4 h-4" />
                    {t("appMenuListHeading")}
                  </label>

                  <div className="space-y-3">
                    {availableMenus.map((menu) => {
                      const quantity = menuQuantities[menu.id] || 0;
                      const isExpanded = expandedMenuId === menu.id;
                      return (
                        <div
                          key={menu.id}
                          className="border border-gray-200 rounded-lg p-3"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-800 truncate">
                                {menu.name}
                              </p>
                              <p className="text-sm text-gray-500">
                                {menu.price.toFixed(2)} €
                              </p>
                            </div>
                          </div>

                          {menu.description && (
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedMenuId(isExpanded ? null : menu.id)
                              }
                              className="mt-1 flex items-center gap-1 text-xs text-primary-600 hover:underline"
                            >
                              {isExpanded ? (
                                <>
                                  {t("commonMenuHideDescription")}
                                  <ChevronUp className="w-3 h-3" />
                                </>
                              ) : (
                                <>
                                  {t("commonMenuShowDescription")}
                                  <ChevronDown className="w-3 h-3" />
                                </>
                              )}
                            </button>
                          )}
                          {isExpanded && menu.description && (
                            <p className="mt-2 text-sm text-gray-600 whitespace-pre-line">
                              {menu.description}
                            </p>
                          )}

                          <div className="mt-3">
                            {menu.scope === "all_guests" ? (
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={
                                    quantity === effectivePartySize &&
                                    quantity > 0
                                  }
                                  onChange={(e) =>
                                    setAllGuestsMenuQuantity(
                                      menu.id,
                                      e.target.checked,
                                    )
                                  }
                                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-sm text-gray-700">
                                  {t("commonMenuAllGuestsLabel", {
                                    count: effectivePartySize,
                                  })}
                                </span>
                              </label>
                            ) : (
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  onClick={() => decrementMenuQuantity(menu.id)}
                                  disabled={quantity <= 0}
                                  className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                                <span className="w-6 text-center font-semibold text-gray-800">
                                  {quantity}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => incrementMenuQuantity(menu.id)}
                                  disabled={
                                    totalMenuQuantity >= effectivePartySize
                                  }
                                  className="w-8 h-8 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-sm text-gray-500 px-1">
                    {t("commonMenuTotalLabel", {
                      total: totalMenuQuantity,
                      count: effectivePartySize,
                    })}
                  </p>

                  {menuQuantityExceedsPartySize && (
                    <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                      {t("commonMenuExceedsPartySize")}
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setWantsMenu(null)}
                      className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                    >
                      {t("appBackButton")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep("details")}
                      disabled={menuQuantityExceedsPartySize}
                      className="flex-1 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed uppercase tracking-wide"
                    >
                      {t("appContinueButton")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === "details" && (
            <div className="p-4 space-y-4">
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-primary-800">
                    <Calendar className="w-4 h-4" />
                    <span className="font-medium">
                      {formatDate(new Date(selectedDate + "T12:00:00"), "short")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-primary-800">
                    <Clock className="w-4 h-4" />
                    <span className="font-medium">{selectedTime}</span>
                  </div>
                  <div className="flex items-center gap-2 text-primary-800">
                    <Users className="w-4 h-4" />
                    <span className="font-medium">
                      {tPeopleShort(effectivePartySize)}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setStep("selection")}
                  className="text-xs text-primary-600 hover:underline mt-2"
                >
                  {t("appSummaryModify")}
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("appPhoneLabel")}
                </label>
                <div className="flex gap-2 w-full">
                  <select
                    value={customerCountryCode}
                    onChange={(e) => setCustomerCountryCode(e.target.value)}
                    className="w-20 sm:w-24 flex-shrink-0 px-2 py-2.5 border border-gray-300 rounded-lg bg-white text-sm"
                  >
                    {orderedCountries.map((country) => (
                      <option key={country.iso} value={country.code}>
                        {country.flag} {country.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder={t("appPhonePlaceholder")}
                    className="flex-1 min-w-0 px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("appNameLabel")}
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder={t("appNamePlaceholder")}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("appLastNameLabel")}
                  </label>
                  <input
                    type="text"
                    value={customerLastName}
                    onChange={(e) => setCustomerLastName(e.target.value)}
                    placeholder={t("appLastNamePlaceholder")}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("appEmailLabel")}
                </label>
                <input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => {
                    setCustomerEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  onBlur={() => {
                    if (customerEmail && !isValidEmail(customerEmail))
                      setEmailError(t("appEmailInvalid"));
                  }}
                  placeholder={t("appEmailPlaceholder")}
                  className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 ${emailError ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                  required
                />
                {emailError && (
                  <p className="text-red-500 text-xs mt-1">{emailError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("appNotesLabel")}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={
                    scheduleConfig?.notesPlaceholder ||
                    t("appNotesPlaceholderDefault")
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>

              {submitError && (
                <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                  {submitError}
                </div>
              )}

              <div className="space-y-2 pt-2 border-t border-gray-100 text-sm">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="w-4 h-4 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-gray-600">
                    {t("appAcceptTermsPrefix")}{" "}
                    <a
                      href="https://reservamanager.com/terminos"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      {t("appTermsLinkLabel")}
                    </a>
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptPrivacy}
                    onChange={(e) => setAcceptPrivacy(e.target.checked)}
                    className="w-4 h-4 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-gray-600">
                    {t("appAcceptPrivacyPrefix")}{" "}
                    <a
                      href="https://reservamanager.com/privacidad-comensal"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      {t("appPrivacyLinkLabel")}
                    </a>
                  </span>
                </label>
                {scheduleConfig?.spendNotice && (
                  <p className="text-xs text-gray-500 pl-6">
                    {t("appSpendNotice", { restaurant: organization?.name ?? "" })}{" "}
                    <a
                      href="https://reservamanager.com/p"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:underline"
                    >
                      reservamanager.com/p
                    </a>
                  </p>
                )}
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptMarketing}
                    onChange={(e) => setAcceptMarketing(e.target.checked)}
                    className="w-4 h-4 mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-gray-500">
                    {t("appMarketingCheckbox")}
                  </span>
                </label>
              </div>

              {isLargeGroup && (
                <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3 text-sm">
                  <div className="text-secondary-700 font-medium flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    {t("appLargeGroupSummary", {
                      people: tPeople(effectivePartySize),
                    })}
                  </div>
                  <p className="text-secondary-600 mt-1">
                    {t("commonRestaurantConfirmAvailability")}
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    // Si ese día tenía menús, "Atrás" vuelve al paso "menu"
                    // (conserva las cantidades ya elegidas en el estado) en
                    // vez de saltárselo directo a "selection".
                    if (availableMenus.length > 0) {
                      setWantsMenu(totalMenuQuantity > 0 ? true : null);
                      setStep("menu");
                    } else {
                      setStep("selection");
                    }
                  }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  {t("appBackButton")}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={
                    !customerName ||
                    !customerLastName ||
                    !customerEmail ||
                    !isValidEmail(customerEmail) ||
                    !customerPhone ||
                    !acceptTerms ||
                    !acceptPrivacy ||
                    submitting
                  }
                  className="flex-1 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-wide"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t("appSubmitting")}
                    </>
                  ) : (
                    t("appSubmitButton")
                  )}
                </button>
              </div>
            </div>
          )}
        </div>

        <PoweredBy slug={slug} etiqueta={t("commonPoweredBy")} className="mt-4" />
      </div>

      {/* Full Calendar Modal */}
      {showFullCalendar && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-900">
                {t("appFullCalendarTitle")}
              </h3>
              <button
                onClick={() => setShowFullCalendar(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              {/* Month navigation */}
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
                  disabled={isBefore(
                    startOfMonth(calendarMonth),
                    startOfMonth(today),
                  )}
                  className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="font-semibold text-gray-900">
                  {formatDate(calendarMonth, "monthYear")}
                </span>
                <button
                  onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
                  disabled={isAfter(
                    startOfMonth(addMonths(calendarMonth, 1)),
                    maxDate,
                  )}
                  className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Weekday headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {weekdays.map((day, idx) => (
                  <div
                    key={idx}
                    className="text-center text-xs font-medium text-gray-500 py-1"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {(() => {
                  const monthStart = startOfMonth(calendarMonth);
                  const monthEnd = endOfMonth(calendarMonth);
                  const days = eachDayOfInterval({
                    start: monthStart,
                    end: monthEnd,
                  });

                  // Add empty cells for days before month starts (Monday = 0)
                  const startDay = getDay(monthStart);
                  const emptyDays = startDay === 0 ? 6 : startDay - 1;

                  return (
                    <>
                      {Array.from({ length: emptyDays }).map((_, i) => (
                        <div key={`empty-${i}`} className="aspect-square" />
                      ))}
                      {days.map((day) => {
                        const dateStr = format(day, "yyyy-MM-dd");
                        const isSelected = selectedDate === dateStr;
                        const isPast = isBefore(
                          startOfDay(day),
                          startOfDay(today),
                        );
                        const isFuture = isAfter(
                          startOfDay(day),
                          startOfDay(maxDate),
                        );
                        const dayAvailability = calendarAvailability[dateStr];
                        const isClosed =
                          dayAvailability && !dayAvailability.isOpen;
                        const isDisabled = isPast || isFuture || isClosed;

                        return (
                          <button
                            key={dateStr}
                            type="button"
                            onClick={() => {
                              if (!isDisabled) {
                                setSelectedDate(dateStr);
                                setShowFullCalendar(false);
                              }
                            }}
                            disabled={isDisabled}
                            className={`aspect-square rounded-lg text-sm font-medium transition-all flex items-center justify-center ${
                              isSelected
                                ? "bg-primary-500 text-white shadow-md"
                                : isDisabled
                                  ? "text-gray-300 cursor-not-allowed"
                                  : "text-gray-700 hover:bg-primary-100"
                            }`}
                            title={dayAvailability?.reason || undefined}
                          >
                            {format(day, "d")}
                          </button>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
